import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { lagerCompaniesAPI } from '../services/api';
import { LagerCompany } from '../types';
import { showCustomAlert } from './CustomAlert';

type CompanyFormState = {
  name: string;
};

const LagerCompanies: React.FC = () => {
  const [companies, setCompanies] = useState<LagerCompany[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState<LagerCompany | null>(null);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState<CompanyFormState>({ name: '' });

  const dialogHeader = useMemo(() => (editing ? 'Izmeni firmu' : 'Dodaj firmu'), [editing]);

  const loadCompanies = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await lagerCompaniesAPI.getAll();
      setCompanies(data);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri učitavanju firmi';
      setError(msg);
      showCustomAlert.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    setFormData({ name: '' });
    setError('');
    setDialogVisible(true);
  };

  const openEdit = (company: LagerCompany) => {
    setEditing(company);
    setFormData({ name: company.name || '' });
    setError('');
    setDialogVisible(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Naziv firme je obavezan');
      return;
    }
    try {
      setError('');
      if (editing?._id) {
        await lagerCompaniesAPI.update(editing._id, { name: formData.name.trim() });
      } else {
        await lagerCompaniesAPI.create({ name: formData.name.trim() });
      }
      setDialogVisible(false);
      await loadCompanies();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri čuvanju firme';
      setError(msg);
      showCustomAlert.error(msg);
    }
  };

  const handleDelete = (company: LagerCompany) => {
    showCustomAlert.confirm(
      `Da li ste sigurni da želite da obrišete firmu "${company.name}"?`,
      async () => {
        try {
          await lagerCompaniesAPI.delete(company._id);
          await loadCompanies();
        } catch (err: any) {
          const msg = err?.response?.data?.message || err?.message || 'Greška pri brisanju firme';
          showCustomAlert.error(msg);
        }
      },
      'Potvrda brisanja'
    );
  };

  const actionsBodyTemplate = (rowData: LagerCompany) => (
    <div className="flex gap-2 justify-content-end">
      <Button
        icon="pi pi-pencil"
        className="action-icon-btn"
        text
        rounded
        onClick={() => openEdit(rowData)}
        tooltip="Izmeni"
      />
      <Button
        icon="pi pi-trash"
        className="action-icon-btn"
        text
        rounded
        severity="danger"
        onClick={() => handleDelete(rowData)}
        tooltip="Obriši"
      />
    </div>
  );

  return (
    <div style={{ padding: '1.5rem' }}>
      <Card
        title="Firme za transport"
        subTitle="Firme"
      >
        {error && (
          <div style={{ marginBottom: '1rem' }}>
            <Message severity="error" text={error} />
          </div>
        )}

        <div className="flex justify-content-end" style={{ marginBottom: '1rem' }}>
          <Button label="Dodaj firmu" icon="pi pi-plus" onClick={openCreate} />
        </div>

        <DataTable value={companies} loading={loading} paginator rows={10} rowsPerPageOptions={[10, 25, 50]} responsiveLayout="scroll">
          <Column field="name" header="Naziv firme" sortable />
          <Column header="Akcije" body={actionsBodyTemplate} style={{ width: '140px' }} />
        </DataTable>
      </Card>

      <Dialog
        header={dialogHeader}
        visible={dialogVisible}
        style={{ width: '520px', maxWidth: '92vw' }}
        onHide={() => setDialogVisible(false)}
        closable
        modal
      >
        <div className="p-fluid">
          <label htmlFor="companyName" style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>
            Naziv firme
          </label>
          <InputText
            id="companyName"
            value={formData.name}
            onChange={(e) => setFormData({ name: e.target.value })}
            placeholder="Unesite naziv firme"
          />

          <div className="flex gap-2 justify-content-end" style={{ marginTop: '1.25rem' }}>
            <Button
              label={editing ? 'Sačuvaj' : 'Sačuvaj'}
              icon="pi pi-check"
              onClick={handleSubmit}
            />
            <Button
              label="Otkaži"
              icon="pi pi-times"
              severity="secondary"
              outlined
              onClick={() => setDialogVisible(false)}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default LagerCompanies;


